import { App, type DirectiveBinding } from 'vue';

// ============================================================================
// Click Outside Directive
// ============================================================================

type ClickOutsideElement = HTMLElement & {
  clickOutsideEvent?: (event: Event) => void;
};

const clickOutside = {
  beforeMount: (
    el: ClickOutsideElement,
    binding: DirectiveBinding<(event: Event) => void>
  ) => {
    el.clickOutsideEvent = (event: Event) => {
      // here I check that click was outside the el and his children
      if (!(el === event.target || el.contains(event.target as Node))) {
        event.stopPropagation();
        // and if it did, call method provided in attribute value
        binding?.value(event);
      }
    };
    document.addEventListener('click', el.clickOutsideEvent);
  },
  unmounted: (el: ClickOutsideElement) => {
    if (el.clickOutsideEvent) {
      document.removeEventListener('click', el.clickOutsideEvent);
    }
  },
};

// ============================================================================
// Drag Dialog - Global Event Handler Approach
// ============================================================================
// Event listeners are registered at module load time (global scope) rather than
// inside Vue directive hooks. This avoids issues with event listeners not firing
// when registered inside directive mounted hooks.

type DragState = {
  dragging: boolean;
  el: HTMLElement | null;
  diffX: number;
  diffY: number;
};

const dragState: DragState = {
  dragging: false,
  el: null,
  diffX: 0,
  diffY: 0,
};

// Header selectors for drag handles
const DRAG_HANDLE_SELECTORS = [
  '.dialog__header',
  '.two-pane-selector__header',
  '.two-pane-selector__header--right',
  '.single-column__header',
  '.selector-base__header',
  '.item-browser__header',
].join(', ');

// Handle mousedown/touchstart - start drag if on a header
function handleDragStart(e: MouseEvent | TouchEvent) {
  const target = e.target as HTMLElement;

  // Find if click is on/inside a drag handle header
  const header = target.closest(DRAG_HANDLE_SELECTORS) as HTMLElement | null;
  if (!header) return;

  // Find the dialog wrapper to move
  const wrapper = header.closest('.dialog__wrapper') as HTMLElement | null;
  if (!wrapper) return;

  // Start dragging
  dragState.dragging = true;
  dragState.el = wrapper;

  const event = 'touches' in e ? e.touches[0] : e;
  const rect = wrapper.getBoundingClientRect();
  dragState.diffX = event.clientX - rect.left;
  dragState.diffY = event.clientY - rect.top;

  // Set cursor on header
  header.style.cursor = 'move';
}

// Handle mousemove/touchmove - update position if dragging
function handleDragMove(e: MouseEvent | TouchEvent) {
  if (!dragState.dragging || !dragState.el) return;

  const event = 'touches' in e ? e.touches[0] : e;
  const posX = event.clientX - dragState.diffX;
  const posY = event.clientY - dragState.diffY;

  dragState.el.style.left = `${posX}px`;
  dragState.el.style.top = `${posY}px`;
  dragState.el.style.position = 'fixed';
}

// Handle mouseup/touchend - end drag
function handleDragEnd() {
  dragState.dragging = false;
  dragState.el = null;
}

// Register global event listeners at module load time
document.addEventListener('mousedown', handleDragStart, { capture: true });
document.addEventListener('touchstart', handleDragStart, {
  passive: true,
  capture: true,
});
document.addEventListener('mousemove', handleDragMove);
document.addEventListener('touchmove', handleDragMove, { passive: true });
document.addEventListener('mouseup', handleDragEnd);
document.addEventListener('touchend', handleDragEnd, { passive: true });

// The v-drag-dialog directive now only sets the cursor style on the header
// The actual drag handling is done by the global event listeners above
const dragDialog = {
  mounted(el: HTMLElement, binding: { value: boolean }) {
    if (binding && binding.value === false) {
      return;
    }

    // Find the header element to set cursor style
    let header = el.querySelector(DRAG_HANDLE_SELECTORS) as HTMLElement | null;

    // If not found directly, look inside wrapper elements
    if (!header) {
      const wrapper = el.querySelector('.dialog__header-wrapper');
      if (wrapper) {
        header = wrapper.querySelector('.dialog__header') as HTMLElement | null;
      }
    }

    if (header) {
      header.style.cursor = 'move';
    }
  },
};

// ============================================================================
// No Tab Into Directive
// ============================================================================

import noTabInto from '@/directives/noTabInto';

// ============================================================================
// Register Directives
// ============================================================================

export default (app: App) => {
  app.directive('click-outside', clickOutside);
  app.directive('drag-dialog', dragDialog);
  app.directive('no-tab-into', noTabInto);
};
